/**
 * The tutorial's step machine (docs/HINTS-LAYOUT-TUTORIAL.md B5, B6). Holds the
 * full state of the scripted hole, accepts only the current step's taught
 * moves, plays Ada's scripted moves on watch steps, and advances on the
 * timings in B9. Never saved: a refresh starts over and the solo save is
 * never touched.
 */
import { applyAction, legalActions, newGame, redactFor } from '../../engine';
import type { Action, GameState, NewPlayer } from '../../engine';
import { sameAction } from '../game/localGame';
import type { Timers } from '../game/localGame';
import { botDelay } from '../game/pacing';
import { SAY_MS, STACK, STEPS, TUTORIAL_SEED } from './script';
import type { Step } from './script';

export const YOU = 0;

/** B9 timings (ms). */
export const TIMING = {
  afterHuman: 450,
  firstBot: 600,
  afterLastBot: 700,
  /** Step 27 waits for the hole-end sheet; this only guards against a hang. */
  sheetFallback: 9000,
};

const browserTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};

export function tutorialPlayers(name: string): NewPlayer[] {
  return [
    { id: 'you', name: name || 'You', isBot: false },
    { id: 'ada', name: 'Ada', isBot: true, botLevel: 'easy' },
  ];
}

export function newTutorialState(name: string): GameState {
  return newGame({ seed: TUTORIAL_SEED, totalHoles: 1, players: tutorialPlayers(name), stack: STACK, options: { tieBreak: 'shared' } });
}

export interface TutorialGameOptions {
  name?: string;
  timers?: Timers;
  /** Scales every delay (?speed=0.2 for testing). */
  speed?: number;
  steps?: Step[];
}

export class TutorialGame {
  private st: GameState;
  private idx = 0;
  /** Allowed actions already applied in the current do step. */
  private applied: Action[] = [];
  private handles: unknown[] = [];
  private running = false;
  private readonly timers: Timers;
  private readonly speed: number;
  private readonly name: string;
  readonly steps: Step[];
  onChange: (() => void) | undefined;

  constructor(opts: TutorialGameOptions = {}) {
    this.name = opts.name ?? 'You';
    this.timers = opts.timers ?? browserTimers;
    this.speed = opts.speed ?? 1;
    this.steps = opts.steps ?? STEPS;
    this.st = newTutorialState(this.name);
  }

  /** Full state (tests); the table only ever sees `view`. */
  get state(): GameState {
    return this.st;
  }

  get view(): GameState {
    return redactFor(this.st, YOU);
  }

  get stepIndex(): number {
    return this.idx;
  }

  get step(): Step {
    return this.steps[this.idx] as Step;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.enter(this.idx);
  }

  stop(): void {
    this.running = false;
    this.clearTimers();
  }

  /** Back to step 1 with a fresh deal. */
  restart(): void {
    this.clearTimers();
    this.st = newTutorialState(this.name);
    this.idx = 0;
    this.applied = [];
    this.running = true;
    this.enter(0);
    this.onChange?.();
  }

  /** The human moves the current step allows right now (empty outside do steps or once the step is complete). */
  allowedNow(): Action[] {
    const step = this.step;
    if (step.kind !== 'do' || this.applied.length >= (step.need ?? 1)) return [];
    const legal = legalActions(this.st, YOU);
    return (step.allow ?? []).filter((a) => legal.some((l) => sameAction(l, a)) && !this.applied.some((x) => sameAction(x, a)));
  }

  /** A human move. Returns false (and changes nothing) unless the step teaches exactly this move. */
  act(action: Action): boolean {
    if (!this.running || !this.allowedNow().some((a) => sameAction(a, action))) return false;
    this.st = applyAction(this.st, action);
    this.applied.push(action);
    if (this.applied.length >= (this.step.need ?? 1)) {
      const at = this.idx;
      this.after(TIMING.afterHuman, () => this.advanceFrom(at));
    }
    this.onChange?.();
    return true;
  }

  /** Next on a tap step, or a tap on a say step's bubble. */
  next(): boolean {
    const step = this.step;
    if (!this.running || !(step.kind === 'tap' || (step.kind === 'say' && !step.untilSheet))) return false;
    this.advanceFrom(this.idx);
    return true;
  }

  /** The table opened its hole-end sheet (ends step 27). */
  sheetOpened(): void {
    if (this.running && this.step.untilSheet) this.advanceFrom(this.idx);
  }

  // ------------------------------------------------------------------ internals

  private after(ms: number, fn: () => void): void {
    const h = this.timers.set(() => {
      this.handles = this.handles.filter((x) => x !== h);
      if (this.running) fn();
    }, Math.round(ms * this.speed));
    this.handles.push(h);
  }

  private clearTimers(): void {
    for (const h of this.handles) this.timers.clear(h);
    this.handles = [];
  }

  /** Advance only if we are still on step `at` (a stale timer never skips a step). */
  private advanceFrom(at: number): void {
    if (at !== this.idx || this.idx >= this.steps.length - 1) return;
    this.clearTimers();
    this.enter(this.idx + 1);
    this.onChange?.();
  }

  private enter(i: number): void {
    this.idx = i;
    this.applied = [];
    const step = this.step;
    if (step.kind === 'watch') this.playBot(step.bot ?? [], 0);
    else if (step.kind === 'say') {
      if (step.untilSheet) this.after(TIMING.sheetFallback, () => this.advanceFrom(i));
      else this.after(step.ms ?? SAY_MS, () => this.advanceFrom(i));
    }
  }

  private playBot(actions: Action[], k: number): void {
    const at = this.idx;
    const action = actions[k];
    if (!action) {
      this.after(TIMING.afterLastBot, () => this.advanceFrom(at));
      return;
    }
    const delay = k === 0 ? TIMING.firstBot : botDelay({ action, state: this.st, afterHuman: false, speed: 1, rand: () => 0.5 });
    this.after(delay, () => {
      if (at !== this.idx) return;
      this.st = applyAction(this.st, action);
      this.onChange?.();
      this.playBot(actions, k + 1);
    });
  }
}
